// ============================================
// pages/Salon/SalonStaff.js
// Staff Management — Add, View, Edit, Delete
// ============================================
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../common/context/AuthContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SALON_API_URL } from '../../api/config';
import { Plus, X, Edit2, Trash2, Search, Users, Loader2, Phone, Mail, Star,
  User,  ChevronDown, RefreshCw, MoreVertical, CheckSquare, Square} from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────

const SKILL_LEVELS = ['Beginner', 'Intermediate', 'Advanced'];

const GENDER_OPTIONS = ['Men', 'Women', 'Unisex'];

const CATEGORY_OPTIONS = [
  'Skin', 'Hair', 'Waxing', 'Nails', 'Massage', 'Makeup', 'Mens',
];

const SKILL_CONFIG = {
  Beginner:     { color: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',   dot: 'bg-green-500',  stars: 1 },
  Intermediate: { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300', dot: 'bg-yellow-500', stars: 2 },
  Advanced:     { color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300', dot: 'bg-purple-500', stars: 3 },
};

const GENDER_COLORS = {
  Men:    'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  Women:  'bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300',
  Unisex: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
};

const CAT_COLORS = {
  Skin:    'bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300',
  Hair:    'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-300',
  Waxing:  'bg-orange-50 text-orange-600 dark:bg-orange-900/30 dark:text-orange-300',
  Nails:   'bg-pink-50 text-pink-600 dark:bg-pink-900/30 dark:text-pink-300',
  Massage: 'bg-cyan-50 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-300',
  Makeup:  'bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-300',
  Mens:    'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300',
};

const EMPTY_FORM = {
  name:               '',
  skill_level:        'Beginner',
  genders:            [],
  categories:         [],
  phone:              '',
  email:              '',
  commission_percent: '',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name = '') {
  return name.trim().split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

function avatarColor(name = '') {
  const colors = [
    'bg-violet-500', 'bg-blue-500', 'bg-emerald-500',
    'bg-rose-500', 'bg-amber-500', 'bg-cyan-500', 'bg-pink-500',
  ];
  const idx = name.charCodeAt(0) % colors.length;
  return colors[idx] || 'bg-slate-500';
}

// ─── Multi-select chip component ──────────────────────────────────────────────

function ChipSelect({ label, options, value, onChange, colorMap, required, error, selectAllLabel }) {
  const allSelected = value.length === options.length;

  const toggle = (opt) => {
    if (value.includes(opt)) onChange(value.filter(v => v !== opt));
    else onChange([...value, opt]);
  };

  const toggleAll = () => {
    if (allSelected) onChange([]);
    else onChange([...options]);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <button
          type="button"
          onClick={toggleAll}
          className="text-xs text-purple-600 dark:text-purple-400 font-semibold hover:underline flex items-center gap-1"
        >
          {allSelected
            ? <><CheckSquare size={12} /> Deselect All</>
            : <><Square size={12} /> {selectAllLabel || 'Select All'}</>
          }
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {options.map(opt => {
          const active  = value.includes(opt);
          const optColor = colorMap?.[opt];
          return (
            <button
              key={opt}
              type="button"
              onClick={() => toggle(opt)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold border-2 transition-all flex items-center gap-1.5 ${
                active
                  ? optColor
                    ? `${optColor} border-current`
                    : 'bg-purple-600 text-white border-purple-600'
                  : 'bg-gray-50 dark:bg-slate-800 text-gray-500 dark:text-gray-400 border-transparent hover:border-gray-300 dark:hover:border-slate-600'
              }`}
            >
              {active && <CheckSquare size={11} />}
              {opt}
            </button>
          );
        })}
      </div>
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
}

// ─── Staff Card ───────────────────────────────────────────────────────────────

function StaffCard({ staff, onEdit, onDelete, onToggle }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const skill  = SKILL_CONFIG[staff.skill_level] || SKILL_CONFIG.Beginner;
  const avatar = avatarColor(staff.name);

  return (
    <div className={`bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm hover:shadow-md transition-all duration-200 p-5 flex flex-col gap-4 ${!staff.isActive ? 'opacity-60' : ''}`}>

      {/* Top: avatar + name + menu */}
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className={`w-12 h-12 rounded-2xl ${avatar} flex items-center justify-center text-white font-black text-base flex-shrink-0`}>
          {getInitials(staff.name)}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-black text-gray-900 dark:text-white text-sm leading-tight truncate">
            {staff.name}
          </h3>
          {/* Skill stars */}
          <div className="flex items-center gap-1 mt-1">
            {[1, 2, 3].map(n => (
              <Star
                key={n}
                size={12}
                className={n <= skill.stars
                  ? 'text-yellow-400 fill-yellow-400'
                  : 'text-gray-200 dark:text-slate-600'}
              />
            ))}
            <span className={`text-xs font-semibold ml-1 px-1.5 py-0.5 rounded-md ${skill.color}`}>
              {staff.skill_level}
            </span>
          </div>
        </div>

        {/* Menu */}
        <div className="relative flex-shrink-0">
          <button
            onClick={() => setMenuOpen(o => !o)}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <MoreVertical size={15} />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-8 z-20 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-100 dark:border-slate-700 py-1 w-40">
                <button onClick={() => { onEdit(staff); setMenuOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700">
                  <Edit2 size={13} /> Edit
                </button>
                <button onClick={() => { onToggle(staff); setMenuOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700">
                  <RefreshCw size={13} /> {staff.isActive ? 'Deactivate' : 'Reactivate'}
                </button>
                <button onClick={() => { onDelete(staff); setMenuOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Genders */}
      {staff.genders?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {staff.genders.map(g => (
            <span key={g} className={`text-xs font-semibold px-2 py-0.5 rounded-full ${GENDER_COLORS[g] || ''}`}>
              {g}
            </span>
          ))}
        </div>
      )}

      {/* Categories */}
      {staff.categories?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {staff.categories.map(c => (
            <span key={c} className={`text-xs font-medium px-2 py-0.5 rounded-lg ${CAT_COLORS[c] || 'bg-gray-100 text-gray-600'}`}>
              {c}
            </span>
          ))}
        </div>
      )}

      {/* Contact */}
      <div className="pt-2 border-t border-gray-50 dark:border-slate-700 space-y-1">
        {staff.phone && (
          <a href={`tel:${staff.phone}`}
            className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors">
            <Phone size={12} /> {staff.phone}
          </a>
        )}
        {staff.email && (
          <a href={`mailto:${staff.email}`}
            className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors truncate">
            <Mail size={12} /> <span className="truncate">{staff.email}</span>
          </a>
        )}
        {staff.commission_percent > 0 && (
          <div className="flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
            <span className="w-3 h-3 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center text-[9px] font-black">%</span>
            {staff.commission_percent}% commission
          </div>
        )}
      </div>

      {/* Inactive badge */}
      {!staff.isActive && (
        <div className="text-center">
          <span className="text-xs bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400 px-3 py-1 rounded-full font-medium">
            Inactive
          </span>
        </div>
      )}
    </div>
  );
}

// ─── Add / Edit Modal ─────────────────────────────────────────────────────────

function StaffModal({ mode, staff, onClose, onSave }) {
  const [form,   setForm]   = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (mode === 'edit' && staff) {
      setForm({
        name:        staff.name        || '',
        skill_level:        staff.skill_level || 'Beginner',
        genders:            staff.genders     || [],
        categories:         staff.categories  || [],
        phone:              staff.phone       || '',
        email:              staff.email       || '',
        commission_percent: staff.commission_percent != null ? String(staff.commission_percent) : '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [mode, staff]);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); };

  const validate = () => {
    const e = {};
    if (!form.name.trim())    e.name  = 'Name is required';
    if (!form.phone.trim())   e.phone = 'Phone number is required';
    if (form.phone && !/^[+]?[\d\s\-().]{7,20}$/.test(form.phone))
      e.phone = 'Invalid phone number';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = 'Invalid email address';
    if (form.genders.length === 0)   e.genders   = 'Select at least one gender';
    if (form.categories.length === 0) e.categories = 'Select at least one category';
    return e;
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      await onSave({
        name:               form.name.trim(),
        skill_level:        form.skill_level,
        genders:            form.genders,
        categories:         form.categories,
        phone:              form.phone.trim(),
        email:              form.email.trim() || null,
        commission_percent: parseFloat(form.commission_percent) || 0,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-lg max-h-[92vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-slate-800 flex-shrink-0">
          <div>
            <h2 className="text-lg font-black text-gray-900 dark:text-white">
              {mode === 'add' ? '👤 Add Staff Member' : '✏️ Edit Staff Member'}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">Fill in the staff details below</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-400 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* Name */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
              Full Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <User size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                className={`w-full bg-gray-50 dark:bg-slate-800 border rounded-xl pl-9 pr-4 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.name ? 'border-red-400' : 'border-gray-200 dark:border-slate-700'}`}
                placeholder="e.g. Priya Sharma"
                value={form.name}
                onChange={e => set('name', e.target.value)}
              />
            </div>
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          {/* Skill Level */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">
              Skill Level <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {SKILL_LEVELS.map(level => {
                const cfg    = SKILL_CONFIG[level];
                const active = form.skill_level === level;
                return (
                  <button key={level} onClick={() => set('skill_level', level)}
                    className={`flex flex-col items-center gap-2 py-3 px-2 rounded-xl border-2 transition-all ${
                      active
                        ? `${cfg.color} border-current`
                        : 'border-gray-200 dark:border-slate-700 text-gray-500 hover:border-gray-300 dark:hover:border-slate-600'
                    }`}
                  >
                    {/* Stars */}
                    <div className="flex gap-0.5">
                      {[1, 2, 3].map(n => (
                        <Star key={n} size={12}
                          className={n <= cfg.stars
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-gray-200 dark:text-slate-600'}
                        />
                      ))}
                    </div>
                    <span className="text-xs font-bold">{level}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Gender (multi-select) */}
          <ChipSelect
            label="Works For (Gender)"
            options={GENDER_OPTIONS}
            value={form.genders}
            onChange={v => set('genders', v)}
            colorMap={GENDER_COLORS}
            required
            error={errors.genders}
            selectAllLabel="Select All Genders"
          />

          {/* Categories (multi-select) */}
          <ChipSelect
            label="Service Categories"
            options={CATEGORY_OPTIONS}
            value={form.categories}
            onChange={v => set('categories', v)}
            colorMap={CAT_COLORS}
            required
            error={errors.categories}
            selectAllLabel="Select All Categories"
          />

          {/* Phone + Email */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Phone size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="tel"
                  className={`w-full bg-gray-50 dark:bg-slate-800 border rounded-xl pl-9 pr-4 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.phone ? 'border-red-400' : 'border-gray-200 dark:border-slate-700'}`}
                  placeholder="+91 98765 43210"
                  value={form.phone}
                  onChange={e => set('phone', e.target.value)}
                />
              </div>
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                Email <span className="font-normal normal-case text-gray-400">(optional)</span>
              </label>
              <div className="relative">
                <Mail size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  className={`w-full bg-gray-50 dark:bg-slate-800 border rounded-xl pl-9 pr-4 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 ${errors.email ? 'border-red-400' : 'border-gray-200 dark:border-slate-700'}`}
                  placeholder="priya@example.com"
                  value={form.email}
                  onChange={e => set('email', e.target.value)}
                />
              </div>
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1.5 uppercase tracking-wide">
                Commission % <span className="font-normal normal-case text-gray-400">(optional)</span>
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-bold">%</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  className="w-full bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl pl-8 pr-4 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="0"
                  value={form.commission_percent}
                  onChange={e => set('commission_percent', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 dark:border-slate-800 flex gap-3 flex-shrink-0">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-semibold text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm font-bold transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Users size={15} />}
            {saving ? 'Saving...' : mode === 'add' ? 'Add Staff' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Delete Modal ─────────────────────────────────────────────────────────────

function DeleteModal({ staff, onClose, onConfirm, loading }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm p-6 flex flex-col gap-4">
        <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-2xl flex items-center justify-center">
          <Trash2 size={22} className="text-red-500" />
        </div>
        <div>
          <h3 className="font-black text-gray-900 dark:text-white">Remove Staff Member?</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Permanently remove <strong className="text-gray-700 dark:text-gray-300">{staff.name}</strong> from your team? This cannot be undone.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 text-sm font-semibold text-gray-600 hover:bg-gray-50 dark:hover:bg-slate-800">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function SalonStaff() {
  const { userToken: token } = useAuth();

  const [staff,        setStaff]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [searchQuery,  setSearchQuery]  = useState('');
  const [filterSkill,  setFilterSkill]  = useState('All');
  const [filterGender, setFilterGender] = useState('All');
  const [filterCat,    setFilterCat]    = useState('All');
  const [showInactive, setShowInactive] = useState(false);
  const [modal,        setModal]        = useState(null);
  const [deleting,     setDeleting]     = useState(false);

  const headers = {
    Authorization:  `Bearer ${token}`,
    'Content-Type': 'application/json',
    Accept:         'application/json',
  };

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchStaff = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterSkill  !== 'All') params.set('skill_level', filterSkill);
      if (filterGender !== 'All') params.set('gender',      filterGender);
      if (filterCat    !== 'All') params.set('category',    filterCat);
      if (showInactive)           params.set('active',      'false');

      const res  = await fetch(`${SALON_API_URL}/staff?${params}`, { headers });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed');
      setStaff(data.data);
    } catch (err) {
      toast.error(err.message || 'Could not load staff');
    } finally {
      setLoading(false);
    }
  }, [token, filterSkill, filterGender, filterCat, showInactive]); // eslint-disable-line

  useEffect(() => { if (token) fetchStaff(); }, [fetchStaff, token]);

  // ── CRUD ───────────────────────────────────────────────────────────────────

  const handleCreate = async (payload) => {
    const res  = await fetch(`${SALON_API_URL}/staff`, { method: 'POST', headers, body: JSON.stringify(payload) });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.message || 'Failed to create');
    toast.success('Staff member added! 👤');
    setModal(null);
    fetchStaff();
  };

  const handleUpdate = async (payload) => {
    const res  = await fetch(`${SALON_API_URL}/staff/${modal.staff.id}`, { method: 'PUT', headers, body: JSON.stringify(payload) });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.message || 'Failed to update');
    toast.success('Staff updated ✅');
    setModal(null);
    fetchStaff();
  };

  const handleToggle = async (member) => {
    try {
      const url    = member.isActive
        ? `${SALON_API_URL}/staff/${member.id}`
        : `${SALON_API_URL}/staff/${member.id}/reactivate`;
      const method = member.isActive ? 'DELETE' : 'PATCH';
      const res    = await fetch(url, { method, headers });
      const data   = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed');
      toast.success(member.isActive ? 'Staff deactivated' : 'Staff reactivated ✅');
      fetchStaff();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res  = await fetch(`${SALON_API_URL}/staff/${modal.staff.id}?permanent=true`, { method: 'DELETE', headers });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed');
      toast.success('Staff removed');
      setModal(null);
      fetchStaff();
    } catch (err) {
      toast.error(err.message || 'Could not delete');
    } finally {
      setDeleting(false);
    }
  };

  // ── Derived ────────────────────────────────────────────────────────────────

  const filtered = staff.filter(s => {
    const q = searchQuery.toLowerCase();
    return (
      s.name?.toLowerCase().includes(q) ||
      s.phone?.toLowerCase().includes(q) ||
      s.email?.toLowerCase().includes(q) ||
      s.skill_level?.toLowerCase().includes(q)
    );
  });

  // Group by skill level
  const grouped = SKILL_LEVELS.reduce((acc, level) => {
    const items = filtered.filter(s => s.skill_level === level);
    if (items.length) acc[level] = items;
    return acc;
  }, {});
  // Add ungrouped (no skill_level match — edge case)
  const ungrouped = filtered.filter(s => !SKILL_LEVELS.includes(s.skill_level));
  if (ungrouped.length) grouped['Other'] = ungrouped;

  const totalActive      = staff.filter(s => s.isActive).length;
  const beginnerCount    = staff.filter(s => s.skill_level === 'Beginner'     && s.isActive).length;
  const intermediateCount= staff.filter(s => s.skill_level === 'Intermediate' && s.isActive).length;
  const advancedCount    = staff.filter(s => s.skill_level === 'Advanced'     && s.isActive).length;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="p-6 min-h-full">
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
        <div className="flex-1">
          <h1 className="text-2xl font-black text-gray-900 dark:text-white flex items-center gap-2">
            <Users size={22} className="text-purple-600" />
            Staff Management
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage your salon team · {totalActive} active
          </p>
        </div>
        <button
          onClick={() => setModal({ type: 'add' })}
          className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-lg shadow-purple-200 dark:shadow-purple-900/30 transition-all hover:scale-105"
        >
          <Plus size={16} /> Add Staff
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total Active',  value: totalActive,       color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-900/20'  },
          { label: 'Beginner',      value: beginnerCount,     color: 'text-green-600 dark:text-green-400',   bg: 'bg-green-50 dark:bg-green-900/20'    },
          { label: 'Intermediate',  value: intermediateCount, color: 'text-yellow-600 dark:text-yellow-400', bg: 'bg-yellow-50 dark:bg-yellow-900/20'  },
          { label: 'Advanced',      value: advancedCount,     color: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-50 dark:bg-violet-900/20'  },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-2xl p-4 text-center`}>
            <p className={`text-2xl font-black ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 p-4 mb-6 flex flex-wrap gap-3">

        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholder="Search by name, phone, skill..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Skill filter */}
        <div className="flex gap-1.5">
          {['All', ...SKILL_LEVELS].map(s => (
            <button key={s} onClick={() => setFilterSkill(s)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                filterSkill === s
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-600'
              }`}
            >{s}</button>
          ))}
        </div>

        {/* Gender filter */}
        <div className="flex gap-1.5">
          {['All', ...GENDER_OPTIONS].map(g => (
            <button key={g} onClick={() => setFilterGender(g)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all ${
                filterGender === g
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-50 dark:bg-slate-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-600'
              }`}
            >{g}</button>
          ))}
        </div>

        {/* Category filter */}
        <div className="relative">
          <select value={filterCat} onChange={e => setFilterCat(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2 rounded-xl text-xs font-semibold bg-gray-50 dark:bg-slate-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-purple-500 cursor-pointer">
            <option value="All">All Categories</option>
            {CATEGORY_OPTIONS.map(c => <option key={c}>{c}</option>)}
          </select>
          <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>

        {/* Inactive toggle */}
        <button onClick={() => setShowInactive(v => !v)}
          className={`px-3 py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5 ${
            showInactive
              ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'
              : 'bg-gray-50 dark:bg-slate-700 text-gray-500 dark:text-gray-400'
          }`}
        >
          <RefreshCw size={12} /> {showInactive ? 'Showing all' : 'Active only'}
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <Loader2 size={28} className="animate-spin text-purple-600" />
          <p className="text-sm text-gray-400">Loading staff...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 gap-4">
          <div className="w-16 h-16 bg-purple-50 dark:bg-purple-900/20 rounded-2xl flex items-center justify-center">
            <Users size={28} className="text-purple-400" />
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-sm font-medium">
            {searchQuery ? 'No staff match your search' : 'No staff members yet'}
          </p>
          {!searchQuery && (
            <button onClick={() => setModal({ type: 'add' })}
              className="text-purple-600 dark:text-purple-400 text-sm font-semibold flex items-center gap-1 hover:underline">
              <Plus size={14} /> Add your first staff member
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([level, members]) => {
            const cfg = SKILL_CONFIG[level] || { color: 'bg-gray-100 text-gray-600', stars: 0 };
            return (
              <div key={level}>
                {/* Group header */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex gap-0.5">
                    {[1, 2, 3].map(n => (
                      <Star key={n} size={14}
                        className={n <= cfg.stars
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-200 dark:text-slate-700'}
                      />
                    ))}
                  </div>
                  <h2 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest">{level}</h2>
                  <span className="text-xs bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full">{members.length}</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {members.map(s => (
                    <StaffCard
                      key={s.id}
                      staff={s}
                      onEdit={m => setModal({ type: 'edit', staff: m })}
                      onDelete={m => setModal({ type: 'delete', staff: m })}
                      onToggle={handleToggle}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {modal?.type === 'add' && (
        <StaffModal mode="add" onClose={() => setModal(null)} onSave={handleCreate} />
      )}
      {modal?.type === 'edit' && (
        <StaffModal mode="edit" staff={modal.staff} onClose={() => setModal(null)} onSave={handleUpdate} />
      )}
      {modal?.type === 'delete' && (
        <DeleteModal staff={modal.staff} onClose={() => setModal(null)} onConfirm={handleDelete} loading={deleting} />
      )}
    </div>
  );
}